"use client";

import {
  Camera,
  Download,
  Plus,
  RefreshCw,
  SquareCheckBig,
  UserRound,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfigurableDataTable } from "@/components/ui/configurable-data-table";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { usePersistentTablePreferences } from "@/components/ui/use-persistent-table-preferences";
import {
  parseStockCountQrPayload,
  type ParsedQrPayload,
} from "@/lib/standalone-stock-count/qr-payload";
import {
  readStandaloneOperator,
  saveStandaloneOperator,
} from "@/lib/standalone-stock-count/operator-storage";
import { StandaloneQrScanner } from "./qr-scanner";

type Entry = {
  id: string;
  code: string;
  material: string;
  quantity: string;
  countedBy: string;
  createdAt: string;
};

type Count = {
  id: string;
  status: "OPEN" | "CLOSED";
  createdAt: string;
  closedAt: string | null;
  entries: Entry[];
};

type Draft = ParsedQrPayload & { entryId?: string; quantity: string };

class StandaloneApiError extends Error {
  details: Record<string, unknown>;

  constructor(message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.details = details;
  }
}

async function apiRequest<T>(path: string, init?: RequestInit) {
  const response = await fetch(`/api/standalone/stock-count${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const contentType = response.headers.get("content-type") ?? "";
  const body = contentType.includes("json")
    ? ((await response.json()) as Record<string, unknown>)
    : null;
  if (!response.ok) {
    throw new StandaloneApiError(
      typeof body?.message === "string"
        ? body.message
        : "Não foi possível concluir a operação.",
      body ?? {},
    );
  }
  return body as T;
}

export function StandaloneStockCountClient() {
  const [operator, setOperator] = useState("");
  const [operatorDraft, setOperatorDraft] = useState("");
  const [operatorModalOpen, setOperatorModalOpen] = useState(false);
  const [count, setCount] = useState<Count | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [manualMaterial, setManualMaterial] = useState("");
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const operatorInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      setCount(await apiRequest<Count>("/current"));
      setMessage("");
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Falha ao carregar a contagem.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const storedOperator = readStandaloneOperator();
      setOperator(storedOperator);
      setOperatorDraft(storedOperator);
      setOperatorModalOpen(!storedOperator);
      void refresh();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  useEffect(() => {
    const interval = window.setInterval(() => void refresh(), 8000);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  useEffect(() => {
    if (operatorModalOpen)
      window.setTimeout(() => operatorInputRef.current?.focus(), 0);
  }, [operatorModalOpen]);

  const handleOperatorSave = () => {
    const next = saveStandaloneOperator(operatorDraft);
    if (!next) return;
    setOperator(next);
    setOperatorModalOpen(false);
  };

  const handleParsedPayload = useCallback(
    (payload: string) => {
      try {
        const parsed = parseStockCountQrPayload(payload);
        const existing = count?.entries.find(
          (entry) => entry.code === parsed.code,
        );
        if (existing) {
          setMessage(
            `Este material já foi contado: ${existing.code} — ${existing.material}; quantidade ${existing.quantity}; por ${existing.countedBy}.`,
          );
          return;
        }
        setDraft({ ...parsed, quantity: "" });
        setMessage("");
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "QR inválido.");
      }
    },
    [count],
  );

  const handleManualSubmit = () => {
    handleParsedPayload(`${manualCode.trim()} - ${manualMaterial.trim()}`);
    setManualOpen(false);
    setManualCode("");
    setManualMaterial("");
  };

  const handleSaveDraft = async () => {
    if (!draft || !operator) return;
    const quantity = Number(draft.quantity.replace(",", "."));
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setMessage("Informe uma quantidade maior que zero.");
      return;
    }
    try {
      if (draft.entryId) {
        await apiRequest(`/current/entries/${draft.entryId}`, {
          method: "PATCH",
          body: JSON.stringify({ quantity }),
        });
      } else {
        await apiRequest("/current/entries", {
          method: "POST",
          body: JSON.stringify({
            code: draft.code,
            material: draft.material,
            quantity,
            countedBy: operator,
          }),
        });
      }
      setDraft(null);
      setMessage("Salvo");
      await refresh();
    } catch (error) {
      if (error instanceof StandaloneApiError && error.details.entry) {
        const original = error.details.entry as Entry;
        setMessage(
          `Este material já foi contado: ${original.code} — ${original.material}; quantidade ${original.quantity}; por ${original.countedBy}.`,
        );
      } else {
        setMessage(
          error instanceof Error ? error.message : "Não foi possível salvar.",
        );
      }
    }
  };

  const handleClose = async () => {
    try {
      setCount(await apiRequest<Count>("/current/close", { method: "POST" }));
      setMessage("Contagem finalizada.");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Não foi possível finalizar.",
      );
    }
  };

  const handleNew = async () => {
    try {
      setCount(await apiRequest<Count>("/new", { method: "POST" }));
      setConfirmNewOpen(false);
      setMessage("Nova contagem aberta.");
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Não foi possível abrir nova contagem.",
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "code" as const,
        label: "Código",
        minWidth: 140,
        width: 180,
        render: (item: Entry) => (
          <span className="font-medium">{item.code}</span>
        ),
        sortValue: (item: Entry) => item.code,
        sortable: true,
      },
      {
        id: "material" as const,
        label: "Material",
        minWidth: 220,
        width: 360,
        render: (item: Entry) => item.material,
        sortValue: (item: Entry) => item.material,
        sortable: true,
      },
      {
        id: "quantity" as const,
        label: "Quantidade",
        minWidth: 120,
        width: 150,
        render: (item: Entry) => item.quantity,
        sortValue: (item: Entry) => Number(item.quantity),
        sortable: true,
      },
    ],
    [],
  );
  const table = usePersistentTablePreferences({
    columns,
    storageKey: "standalone-stock-count-table",
  });

  const isOpen = count?.status === "OPEN";
  const scannerActive = Boolean(
    isOpen && operator && !draft && !manualOpen && !operatorModalOpen,
  );

  return (
    <div className="mx-auto min-h-screen w-full max-w-5xl px-4 py-5 sm:px-6 sm:py-8">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">
            Lightbird
          </p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
            Contagem de estoque
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Contagem física colaborativa por QR code
          </p>
        </div>
        <Badge
          className={
            isOpen
              ? "bg-emerald-50 text-emerald-800"
              : "bg-slate-100 text-slate-700"
          }
        >
          {isOpen ? "Aberta" : "Finalizada"}
        </Badge>
      </header>

      <section className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 text-sm">
            <UserRound className="h-4 w-4 text-slate-500" aria-hidden="true" />
            <span>
              Operador: <strong>{operator || "não informado"}</strong>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOperatorDraft(operator);
                setOperatorModalOpen(true);
              }}
            >
              Alterar
            </Button>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Itens contados: <strong>{count?.entries.length ?? 0}</strong>
          </div>
        </div>

        {message ? (
          <p className="rounded-md bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:bg-sky-950/40 dark:text-sky-100">
            {message}
          </p>
        ) : null}

        {isOpen ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold">Ler material</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Formato esperado: Código - Material
                </p>
              </div>
              <Camera className="h-5 w-5 text-sky-600" aria-hidden="true" />
            </div>
            {scannerActive ? (
              <StandaloneQrScanner active onPayload={handleParsedPayload} />
            ) : (
              <div className="rounded-lg bg-slate-100 px-4 py-10 text-center text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                Leitor pausado enquanto você informa a quantidade.
              </div>
            )}
            <Button
              variant="outline"
              className="mt-4 w-full"
              onClick={() => setManualOpen(true)}
            >
              Digitar manualmente
            </Button>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {isOpen ? (
            <Button variant="outline" onClick={handleClose}>
              <SquareCheckBig className="h-4 w-4" /> Finalizar contagem
            </Button>
          ) : null}
          {!isOpen && count ? (
            <Button onClick={() => setConfirmNewOpen(true)}>
              <Plus className="h-4 w-4" /> Nova contagem
            </Button>
          ) : null}
          {count ? (
            <Button variant="outline" asChild>
              <a href={`/api/standalone/stock-count/${count.id}/export`}>
                <Download className="h-4 w-4" /> Exportar XLSX
              </a>
            </Button>
          ) : null}
          <Button
            variant="ghost"
            onClick={() => void refresh()}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4" /> Atualizar
          </Button>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
            <h2 className="font-semibold">Materiais contados</h2>
          </div>
          <div className="h-[24rem] min-h-0">
            <ConfigurableDataTable
              items={count?.entries ?? []}
              columns={columns}
              columnOrder={table.columnOrder}
              columnWidths={table.columnWidths}
              visibleColumns={table.visibleColumns}
              sort={table.sort}
              moveColumn={table.moveColumn}
              resizeColumn={table.resizeColumn}
              toggleSort={table.toggleSort}
              toggleColumnVisibility={table.toggleColumnVisibility}
              resetPreferences={table.resetPreferences}
              getRowKey={(item) => item.id}
              onRowClick={(item) => {
                if (isOpen) {
                  setDraft({
                    code: item.code,
                    material: item.material,
                    quantity: item.quantity,
                    entryId: item.id,
                  });
                }
              }}
              emptyState={
                <div className="p-8 text-center text-sm text-slate-500">
                  Nenhum material contado ainda.
                </div>
              }
            />
          </div>
        </div>
      </section>

      <Modal
        title="Quem está realizando a contagem?"
        description="O nome fica salvo somente neste dispositivo."
        open={operatorModalOpen}
        onClose={() => operator && setOperatorModalOpen(false)}
        footer={
          <div className="flex justify-end">
            <Button
              onClick={handleOperatorSave}
              disabled={!operatorDraft.trim()}
            >
              Continuar
            </Button>
          </div>
        }
      >
        <Input
          ref={operatorInputRef}
          value={operatorDraft}
          onChange={(event) => setOperatorDraft(event.target.value)}
          placeholder="Nome do operador"
          maxLength={120}
          onKeyDown={(event) => event.key === "Enter" && handleOperatorSave()}
        />
      </Modal>

      <Modal
        title="Digitar material"
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setManualOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleManualSubmit}
              disabled={!manualCode.trim() || !manualMaterial.trim()}
            >
              Continuar
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <Input
            value={manualCode}
            onChange={(event) => setManualCode(event.target.value)}
            placeholder="Código"
            autoFocus
          />
          <Input
            value={manualMaterial}
            onChange={(event) => setManualMaterial(event.target.value)}
            placeholder="Material"
          />
        </div>
      </Modal>

      <Modal
        title={draft?.entryId ? "Editar quantidade" : "Informar quantidade"}
        description={draft ? `${draft.code} — ${draft.material}` : undefined}
        open={Boolean(draft)}
        onClose={() => setDraft(null)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancelar
            </Button>
            <Button onClick={() => void handleSaveDraft()}>Salvar</Button>
          </div>
        }
      >
        <Input
          type="number"
          inputMode="decimal"
          min="0.001"
          step="any"
          autoFocus
          value={draft?.quantity ?? ""}
          onChange={(event) =>
            setDraft((current) =>
              current ? { ...current, quantity: event.target.value } : current,
            )
          }
          placeholder="Quantidade"
        />
      </Modal>

      <Modal
        title="Abrir nova contagem?"
        description="A contagem atual será finalizada e seus dados continuarão disponíveis para exportação."
        open={confirmNewOpen}
        onClose={() => setConfirmNewOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmNewOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => void handleNew()}>Abrir nova</Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Os mesmos códigos poderão ser contados novamente na nova contagem.
        </p>
      </Modal>
    </div>
  );
}
